import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Plan } from '@vault/shared';
import { api, clearTokens, loadTokens, onAuthLost, saveTokens } from '../api/client.js';

export interface SessionUser {
  id: string;
  email: string;
  plan: Plan;
}

interface AuthState {
  user: SessionUser | null;
  ready: boolean;
  isPro: boolean;
  requestLink: (email: string) => Promise<void>;
  verify: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ready, setReady] = useState(false);

  const refreshUser = useCallback(async () => {
    const t = loadTokens();
    if (!t) {
      setUser(null);
      return;
    }
    try {
      const usage = await api.account.usage();
      setUser({ id: '', email: t.email, plan: usage.plan });
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    void refreshUser().finally(() => setReady(true));
    return onAuthLost(() => setUser(null));
  }, [refreshUser]);

  const requestLink = useCallback(async (email: string) => {
    await api.auth.requestLink(email);
  }, []);

  const verify = useCallback(async (token: string) => {
    const res = await api.auth.verify(token);
    saveTokens({
      accessToken: res.accessToken,
      refreshToken: res.refreshToken,
      deviceId: res.deviceId,
      email: res.user.email,
    });
    setUser({ id: res.user.id, email: res.user.email, plan: res.user.plan });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      /* best effort */
    }
    clearTokens();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, ready, isPro: user?.plan === 'pro', requestLink, verify, logout, refreshUser }),
    [user, ready, requestLink, verify, logout, refreshUser],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
